import React, { useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Box, Typography, Chip, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

const LineGraph = ({
  data = [],
  xAxis = "name",
  yAxis = "value",
  title = "Line Chart",
  width = 400,
  height = 300,
  color = "#4ECDC4",
  showLegend = false,
  strokeWidth = 3,
  showDots = true,
  // New props for multi-vehicle support
  isMultiVehicle = false,
  vehicleColors = {},
  timeField = "time",
  speedField = "speed",
  vehicleField = "vehicleName",
  dateRange = "today",
  yAxisLabel = "",
}) => {
  const { t } = useTranslation();
  // Generate colors for vehicles if not provided
  const defaultColors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E9",
    "#F8C471",
    "#82E0AA",
    "#F1948A",
    "#85C1E9",
    "#D2B4DE",
  ];

  // Process data for multi-vehicle display
  const processMultiVehicleData = (rawData) => {
    if (!isMultiVehicle || !Array.isArray(rawData)) {
      return { chartData: data, vehicles: [] };
    }

    // For trips by time of day, the data is already properly structured
    if (
      rawData[0] &&
      ("Trips Start Time" in rawData[0] || "Trips End Time" in rawData[0])
    ) {
      const vehicles = ["Trips Start Time", "Trips End Time"];
      return { chartData: rawData, vehicles };
    }

    // Group data by time and create a unified dataset
    const timeMap = new Map();
    const vehicleSet = new Set();

    // First pass: collect all vehicles and time points
    rawData.forEach((item) => {
      const timeKey = item[timeField] || item.time;
      const vehicle = item[vehicleField] || item.vehicleName || "Unknown";
      vehicleSet.add(vehicle);

      if (!timeMap.has(timeKey)) {
        timeMap.set(timeKey, { time: timeKey });
      }
    });

    // Get all unique vehicles
    const vehicles = Array.from(vehicleSet);

    // Second pass: initialize all vehicle properties for each time point
    timeMap.forEach((timeData, timeKey) => {
      vehicles.forEach((vehicle) => {
        timeData[vehicle] = null; // Initialize with null instead of undefined
      });
    });

    // Third pass: fill in actual data
    rawData.forEach((item) => {
      const timeKey = item[timeField] || item.time;
      const vehicle = item[vehicleField] || item.vehicleName || "Unknown";
      const speed = parseFloat(item[speedField] || item.speed || 0);

      if (timeMap.has(timeKey)) {
        timeMap.get(timeKey)[vehicle] = speed;
      }
    });

    // Convert to array and sort by time
    const chartData = Array.from(timeMap.values()).sort((a, b) => {
      // Try to parse as date first, then as string comparison
      const timeA = new Date(a.time).getTime() || a.time;
      const timeB = new Date(b.time).getTime() || b.time;
      return timeA > timeB ? 1 : timeA < timeB ? -1 : 0;
    });

    return { chartData, vehicles };
  };

  const { chartData, vehicles } = isMultiVehicle
    ? processMultiVehicleData(data)
    : { chartData: data, vehicles: [] };

  const getYAxisLabel = () => {
    if (yAxisLabel) {
      return t(yAxisLabel);
    }

    if (isMultiVehicle) {
      // Check if this is trips by time of day chart
      if (title.includes("Trips by Time of Day")) {
        return t("No. of Trips");
      }

      // Check the title to determine the correct label
      if (title.includes("Overspeed Timeline")) {
        if (dateRange) {
          const isSingleDayView = ["today", "yesterday"].includes(dateRange);
          return isSingleDayView ? t("Speed (km/h)") : t("Average Speed (km/h)");
        }
        return t("Speed (km/h)");
      } else if (title.includes("Trip Timeline")) {
        // Check if it's duration-based timeline
        if (title.includes("Duration") || title.includes("Average Duration")) {
          return t("Average Duration (Minutes)");
        }
        return t("Trip Count"); // Fallback for count-based timelines
      } else if (dateRange) {
        const isSingleDayView = ["today", "yesterday"].includes(dateRange);
        return isSingleDayView ? t("Count") : t("Average Count");
      }
      return t("Count");
    }
    return isMultiVehicle ? t("Speed (km/h)") : t(yAxis);
  };

  // Custom tooltip for multi-vehicle
  // Update in LineGraph.jsx - Custom Tooltip section
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0]?.payload;
      const isWeekend = dataPoint?.isWeekend;
      const isSingleDayView = ["today", "yesterday"].includes(dateRange);

      // Check if this is the trips by time of day chart
      const isTripsByTimeChart = title.includes("Trips by Time of Day");
      const isDurationChart =
        title.includes("Duration") || title.includes("Average Duration");
      const isSpeedChart =
        title.includes("Speed") || title.includes("Overspeed");

      return (
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            maxWidth: "250px",
          }}
        >
          <p
            style={{
              margin: 0,
              fontWeight: "bold",
              color: "#333",
              fontSize: "12px",
            }}
          >
            {isSingleDayView ? `${t("Time")}: ${label}` : `${t("Period")}: ${label}`}
            {isWeekend && (
              <span style={{ color: "#ff9800", marginLeft: "8px" }}>
                ({t("Weekend")})
              </span>
            )}
          </p>
          {payload
            .filter(
              (entry) =>
                entry.value !== null &&
                entry.value !== undefined &&
                entry.value >= 0 // Allow 0 values for trip counts
            )
            .sort((a, b) => b.value - a.value)
            .map((entry, index) => {
              let displayValue = entry.value;
              let unit = "";

              // Format based on chart type
              if (isTripsByTimeChart) {
                unit = ` ${t("trips")}`; // Show "trips" for trip count
              } else if (isDurationChart) {
                unit = ` ${t("mins")}`;
                displayValue = Math.round(entry.value * 10) / 10;
              } else if (isSpeedChart) {
                unit = " km/h";
              } else {
                unit = "";
              }

              return (
                <p
                  key={index}
                  style={{
                    margin: "4px 0 0 0",
                    color: entry.color,
                    fontSize: "11px",
                  }}
                >
                  {`${t(entry.dataKey)}: ${displayValue}${unit}`}
                </p>
              );
            })}
        </div>
      );
    }
    return null;
  };
  // Custom dot component
  const CustomDot = (props) => {
    const { cx, cy } = props;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={3}
        fill={props.stroke}
        stroke="#fff"
        strokeWidth={1}
      />
    );
  };

  // Format time for X-axis
  const formatXAxisTime = (time) => {
    if (!time) return "";

    // If it's already in HH:MM format, return as is
    if (typeof time === "string" && time.includes(":")) {
      return time;
    }

    // Try to parse as date and format
    try {
      const date = new Date(time);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      }
    } catch (e) {
      // Fallback to string representation
    }

    return String(time);
  };

  const formatYAxisTick = useCallback((value) => {
    // Simply return the value as-is since allowDecimals handles integer enforcement
    return value;
  }, []);

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Typography
        variant="h6"
        sx={{
          textAlign: "center",
          mb: 2,
          color: "#183059",
          fontWeight: 600,
          fontSize: "16px",
        }}
      >
        {t(title)}
      </Typography>

      <ResponsiveContainer
        width="100%"
        height={height - (isMultiVehicle ? 0 : 0)} // Reduced subtraction to increase effective height
      >
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 20, left: 20, bottom: 45 }} 
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey={isMultiVehicle ? "time" : xAxis}
            tick={{ fontSize: 11, fill: "#666" }}
            height={50}
            angle={dateRange?.includes("week") ? -45 : 0}
            textAnchor={dateRange?.includes("week") ? "end" : "middle"}
            label={{
              value: isMultiVehicle
                ? t("Time")
                : t(xAxis.charAt(0).toUpperCase() + xAxis.slice(1)),
              position: "insideBottom",
              offset: 5,
              style: {
                textAnchor: "middle",
                fontSize: "11px",
                fill: "#666",
              },
            }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#666" }}
            tickFormatter={formatYAxisTick}
            width={40}
            allowDecimals={false}
            domain={[0, "auto"]} // Start from 0
            label={{
              value: getYAxisLabel(),
              angle: -90,
              position: "insideLeft",
              offset: -5,
              style: { textAnchor: "middle", fontSize: "11px", fill: "#666" },
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && !isMultiVehicle && <Legend />}

          {isMultiVehicle ? (
            // Render multiple lines for each vehicle
            vehicles.map((vehicle, index) => (
              <Line
                key={vehicle}
                type="monotone"
                dataKey={vehicle}
                stroke={
                  vehicleColors[vehicle] ||
                  defaultColors[index % defaultColors.length]
                }
                strokeWidth={2}
                dot={showDots ? <CustomDot /> : false}
                activeDot={{ r: 4, strokeWidth: 1 }}
                name={vehicle}
                connectNulls={true} // This is key - connect lines even when there are null values
                isAnimationActive={false} // Add this to improve performance
              />
            ))
          ) : (
            // Single line for regular usage
            <Line
              type="monotone"
              dataKey={yAxis}
              stroke={color}
              strokeWidth={strokeWidth}
              dot={showDots ? <CustomDot /> : false}
              activeDot={{ r: 6, fill: color, stroke: "#fff", strokeWidth: 2 }}
              name={yAxis.charAt(0).toUpperCase() + yAxis.slice(1)}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Vehicle Legend for Multi-Vehicle Mode */}
      {isMultiVehicle && vehicles.length > 0 && (
        <Box>
          {/* Only show "Vehicles:" label if not Trips by Time of Day chart */}
          {!title.includes("Trips by Time of Day") && (
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: "#666", fontSize: "12px" }}
            >
              {t("Vehicles")}:
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {vehicles.map((vehicle, index) => (
              <Chip
                key={vehicle}
                label={vehicle}
                size="small"
                sx={{
                  backgroundColor:
                    vehicleColors[vehicle] ||
                    defaultColors[index % defaultColors.length],
                  color: "white",
                  fontSize: "10px",
                  height: "24px",
                  "& .MuiChip-label": {
                    px: 1,
                  },
                }}
              />
            ))}
          </Stack>

          {/* Additional info for aggregated views */}
          {dateRange && !["today", "yesterday"].includes(dateRange) && (
            <Typography
              variant="caption"
              sx={{ mt: 1, color: "#999", fontSize: "10px", display: "block" }}
            >
              * {t("Weekend periods are highlighted with orange borders in tooltip")}
              {dateRange.includes("week") &&
                ` | ${t("Values show average incidents per day")}`}
              {dateRange.includes("month") &&
                ` | ${t("Values show average incidents per day")}`}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LineGraph;
